import React from 'react';
import { AlertCircle, LogIn, ShieldAlert } from 'lucide-react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { SessionEndReason } from '../../lib/sessionExpired';

export default function SessionExpiredModal({ open, reason, message, onLoginAgain }) {
  const isInactive = reason === SessionEndReason.INACTIVE;
  const Icon = isInactive ? ShieldAlert : AlertCircle;
  const title = isInactive ? 'Account inactive' : 'Session expired';

  return (
    <AnimatePresence>
      {open ? (
        <div
          className="fixed inset-0 z-[10050] flex items-center justify-center p-4 sm:p-6"
          role="presentation"
        >
          <Motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-[#0b1220]/60 backdrop-blur-sm"
            aria-hidden
            onClick={onLoginAgain}
          />

          <Motion.div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="session-expired-title"
            aria-describedby="session-expired-desc"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="relative w-full max-w-md overflow-hidden rounded-[var(--radius-card)] border border-[var(--sw-surface-200)] bg-white shadow-[0_24px_64px_rgba(10,23,51,0.25)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[var(--sw-gold-300)] via-[var(--sw-gold-500)] to-[var(--sw-sapphire-400)]"
              aria-hidden
            />

            <div className="p-6 sm:p-7">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1B3478] to-[#0A1733] text-[var(--sw-gold-300)] shadow-lg">
                <Icon className="h-7 w-7" strokeWidth={2} aria-hidden />
              </div>

              <p className="text-center font-heading text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Sign-in required
              </p>
              <h2
                id="session-expired-title"
                className="mt-2 text-center font-heading text-xl font-bold text-[#0f172a] sm:text-2xl"
              >
                {title}
              </h2>
              <p
                id="session-expired-desc"
                className="mt-3 text-center text-sm leading-relaxed text-slate-600"
              >
                {message}
              </p>

              <button
                type="button"
                onClick={onLoginAgain}
                className="touch-target mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#F2C247] to-[#E5A91A] px-4 py-3 font-heading text-sm font-semibold text-[#0A1733] shadow-lg transition-all hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-[var(--sw-gold-400)] focus:ring-offset-2 active:scale-[0.98]"
              >
                <LogIn className="h-4 w-4" aria-hidden />
                Sign in again
              </button>

              <p className="mt-4 text-center text-[11px] text-slate-500">
                {isInactive
                  ? 'If you believe this is a mistake, contact your school administrator.'
                  : 'You were signed out to keep your account secure.'}
              </p>
            </div>
          </Motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}
